import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import RoleGuard, { usePermission } from '../roles/RoleGuard';
import FeatureGate from '../../components/FeatureGate';
import axiosInstance from '../../axiosConfig';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { fetchMedia, uploadMedia } from '../media/mediaSlice';
import './DiscountsNew.css';

export default function DiscountsNew() {
  // Permission check
  const canEdit = usePermission('discounts', 'edit');
  const { confirm } = useConfirm();
  const dispatch = useDispatch();
  const mediaState = useSelector(state => state.media || {});
  const mediaList = mediaState.media || [];
  const mediaUploading = !!mediaState.uploading;

  const [discounts, setDiscounts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [modalStep, setModalStep] = useState(0);
  const [filter, setFilter] = useState('all'); // all, active, inactive, expired
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [showHeroMediaPicker, setShowHeroMediaPicker] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'PERCENTAGE',
    value: '',
    minOrderAmount: '',
    maxDiscountAmount: '',
    maxRedemptions: '0',
    usagePerCustomer: '1',
    isActive: true,
    isPublic: true,
    campaignType: 'COUPON',
    autoApply: false,
    showOnStorefront: false,
    bannerTitle: '',
    bannerSubtitle: '',
    bannerPlacement: 'TOP_BAR',
    badgeText: 'SALE',
    heroEnabled: false,
    heroTitle: '',
    heroSubtitle: '',
    heroImage: '',
    heroCtaText: 'Shop deals',
    heroCtaLink: '/shop',
    heroBackgroundColor: '#0d7463',
    heroTextColor: '#ffffff',
    showPromoCode: true,
    priority: '100',
    applicableProducts: [],
    applicableCategories: [],
    startsAt: '',
    endsAt: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchDiscounts();
    fetchAnalytics();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/merchants/products?page=1&limit=250');
      const payload = response.data;
      setProducts(Array.isArray(payload) ? payload : (payload.products || []));
    } catch (error) {
      console.error('Error fetching products for discounts:', error);
    }
  };

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/discounts');
      if (response.data.success) {
        setDiscounts(response.data.discounts);
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axiosInstance.get('/discounts/analytics?period=30d');
      if (response.data.success) {
        setAnalytics(response.data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleOpenModal = (discount = null) => {
    if (discount) {
      setEditingDiscount(discount);
      setFormData({
        code: discount.code,
        name: discount.name || '',
        description: discount.description || '',
        type: discount.type,
        value: discount.value.toString(),
        minOrderAmount: discount.minOrderAmount ? discount.minOrderAmount.toString() : '',
        maxDiscountAmount: discount.maxDiscountAmount ? discount.maxDiscountAmount.toString() : '',
        maxRedemptions: discount.maxRedemptions.toString(),
        usagePerCustomer: discount.usagePerCustomer.toString(),
        isActive: discount.isActive,
        isPublic: discount.isPublic,
        campaignType: discount.campaignType || 'COUPON',
        autoApply: !!discount.autoApply,
        showOnStorefront: !!discount.showOnStorefront,
        bannerTitle: discount.bannerTitle || '',
        bannerSubtitle: discount.bannerSubtitle || '',
        bannerPlacement: discount.bannerPlacement || 'TOP_BAR',
        badgeText: discount.badgeText || 'SALE',
        heroEnabled: !!discount.heroEnabled,
        heroTitle: discount.heroTitle || '',
        heroSubtitle: discount.heroSubtitle || '',
        heroImage: discount.heroImage || '',
        heroCtaText: discount.heroCtaText || 'Shop deals',
        heroCtaLink: discount.heroCtaLink || '/shop',
        heroBackgroundColor: discount.heroBackgroundColor || '#0d7463',
        heroTextColor: discount.heroTextColor || '#ffffff',
        showPromoCode: discount.showPromoCode !== false,
        priority: discount.priority?.toString() || '100',
        applicableProducts: Array.isArray(discount.applicableProducts) ? discount.applicableProducts : [],
        applicableCategories: Array.isArray(discount.applicableCategories) ? discount.applicableCategories : [],
        startsAt: discount.startsAt ? new Date(discount.startsAt).toISOString().slice(0, 16) : '',
        endsAt: discount.endsAt ? new Date(discount.endsAt).toISOString().slice(0, 16) : ''
      });
    } else {
      setEditingDiscount(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        type: 'PERCENTAGE',
        value: '',
        minOrderAmount: '',
        maxDiscountAmount: '',
        maxRedemptions: '0',
        usagePerCustomer: '1',
        isActive: true,
        isPublic: true,
        campaignType: 'COUPON',
        autoApply: false,
        showOnStorefront: false,
        bannerTitle: '',
        bannerSubtitle: '',
        bannerPlacement: 'TOP_BAR',
        badgeText: 'SALE',
        heroEnabled: false,
        heroTitle: '',
        heroSubtitle: '',
        heroImage: '',
        heroCtaText: 'Shop deals',
        heroCtaLink: '/shop',
        heroBackgroundColor: '#0d7463',
        heroTextColor: '#ffffff',
        showPromoCode: true,
        priority: '100',
        applicableProducts: [],
        applicableCategories: [],
        startsAt: new Date().toISOString().slice(0, 16),
        endsAt: ''
      });
    }
    setErrors({});
    setModalStep(0);
    setShowModal(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = 'Code can only contain letters, numbers, underscores, and hyphens';
    }

    if (!formData.value || parseFloat(formData.value) <= 0) {
      newErrors.value = 'Value must be greater than 0';
    }

    if (formData.type === 'PERCENTAGE' && parseFloat(formData.value) > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }

    if (formData.endsAt && formData.startsAt && new Date(formData.endsAt) <= new Date(formData.startsAt)) {
      newErrors.endsAt = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFinalStep) {
      handleNextStep();
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : null,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        maxRedemptions: parseInt(formData.maxRedemptions) || 0,
        usagePerCustomer: parseInt(formData.usagePerCustomer) || 1,
        autoApply: formData.campaignType === 'SALE' ? true : formData.autoApply,
        isPublic: formData.campaignType === 'SALE' ? true : formData.isPublic,
        showOnStorefront: formData.campaignType === 'SALE' ? formData.showOnStorefront : false,
        heroEnabled: formData.campaignType === 'SALE' ? formData.heroEnabled : false,
        priority: parseInt(formData.priority) || 100,
        startsAt: formData.startsAt,
        endsAt: formData.endsAt || null
      };

      if (editingDiscount) {
        await axiosInstance.put(`/discounts/${editingDiscount.id}`, payload);
      } else {
        await axiosInstance.post('/discounts', payload);
      }

      setShowModal(false);
      fetchDiscounts();
      fetchAnalytics();
    } catch (error) {
      console.error('Error saving discount:', error);
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      }
    }
  };

  const handleToggleActive = async (discount) => {
    try {
      await axiosInstance.post(`/discounts/${discount.id}/toggle`);
      fetchDiscounts();
    } catch (error) {
      console.error('Error toggling discount:', error);
    }
  };

  const handleDelete = async (discount) => {
    const confirmed = await confirm(`Are you sure you want to delete the discount "${discount.code}"?`, {
      title: 'Delete Discount',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });

    if (!confirmed) {
      return;
    }

    try {
      await axiosInstance.delete(`/discounts/${discount.id}`);
      fetchDiscounts();
      fetchAnalytics();
    } catch (error) {
      console.error('Error deleting discount:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(value);
  };

  const getDiscountDisplay = (discount) => {
    switch (discount.type) {
      case 'PERCENTAGE':
        return `${discount.value}% OFF`;
      case 'FIXED_AMOUNT':
        return `${formatCurrency(discount.value)} OFF`;
      case 'FREE_SHIPPING':
        return 'FREE SHIPPING';
      case 'BOGO':
        return 'BUY ONE GET ONE';
      default:
        return discount.value;
    }
  };

  const getStatusBadge = (discount) => {
    const now = new Date();
    const starts = new Date(discount.startsAt);
    const ends = discount.endsAt ? new Date(discount.endsAt) : null;

    if (!discount.isActive) {
      return { label: 'Inactive', class: 'status-inactive' };
    }

    if (now < starts) {
      return { label: 'Scheduled', class: 'status-scheduled' };
    }

    if (ends && now > ends) {
      return { label: 'Expired', class: 'status-expired' };
    }

    if (discount.maxRedemptions > 0 && discount.currentRedemptions >= discount.maxRedemptions) {
      return { label: 'Limit Reached', class: 'status-expired' };
    }

    return { label: 'Active', class: 'status-active' };
  };

  const getRedemptionProgress = (discount) => {
    if (discount.maxRedemptions === 0) return 0;
    return (discount.currentRedemptions / discount.maxRedemptions) * 100;
  };

  const categories = Array.from(new Set(products.map((product) => product.category).filter(Boolean))).sort();

  const toggleProductScope = (productId) => {
    setFormData((prev) => {
      const current = new Set(prev.applicableProducts || []);
      if (current.has(productId)) current.delete(productId);
      else current.add(productId);
      return { ...prev, applicableProducts: Array.from(current) };
    });
  };

  const toggleCategoryScope = (category) => {
    setFormData((prev) => {
      const current = new Set(prev.applicableCategories || []);
      if (current.has(category)) current.delete(category);
      else current.add(category);
      return { ...prev, applicableCategories: Array.from(current) };
    });
  };

  const clearSaleScope = () => {
    setFormData((prev) => ({ ...prev, applicableProducts: [], applicableCategories: [] }));
  };

  const modalSteps = formData.campaignType === 'SALE'
    ? ['Discount', 'Storefront', 'Products', 'Schedule']
    : ['Discount', 'Schedule'];
  const currentStep = Math.min(modalStep, modalSteps.length - 1);
  const isFinalStep = currentStep === modalSteps.length - 1;

  const handleCampaignTypeChange = (campaignType) => {
    setModalStep(0);
    if (campaignType === 'SALE') {
      setFormData({
        ...formData,
        campaignType: 'SALE',
        autoApply: true,
        isPublic: true,
        showOnStorefront: true,
        heroEnabled: true,
        badgeText: formData.badgeText || 'SALE',
        bannerTitle: formData.bannerTitle || formData.name || 'Limited-time sale',
        heroTitle: formData.heroTitle || formData.bannerTitle || formData.name || 'Limited-time sale',
        heroSubtitle: formData.heroSubtitle || formData.bannerSubtitle || 'Deals end soon. Prices update automatically at checkout.'
      });
      return;
    }

    setFormData({
      ...formData,
      campaignType: 'COUPON',
      autoApply: false,
      showOnStorefront: false,
      heroEnabled: false
    });
  };

  const validateCurrentStep = () => {
    if (currentStep !== 0) {
      return true;
    }

    const newErrors = {};
    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = 'Code can only contain letters, numbers, underscores, and hyphens';
    }

    if (!formData.value || parseFloat(formData.value) <= 0) {
      newErrors.value = 'Value must be greater than 0';
    }

    if (formData.type === 'PERCENTAGE' && parseFloat(formData.value) > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (!validateCurrentStep()) {
      return;
    }
    setErrors((prev) => ({ ...prev, code: undefined, value: undefined }));
    setModalStep((step) => Math.min(step + 1, modalSteps.length - 1));
  };

  const openHeroMediaPicker = () => {
    dispatch(fetchMedia({ params: { type: 'image' } }));
    setShowHeroMediaPicker(true);
  };

  const handleHeroMediaSelect = (mediaUrl) => {
    setFormData((prev) => ({ ...prev, heroImage: mediaUrl }));
    setShowHeroMediaPicker(false);
  };

  const handleHeroImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, heroImage: 'Please upload an image file.' }));
      event.target.value = '';
      return;
    }

    try {
      const result = await dispatch(uploadMedia({ file })).unwrap();
      if (result?.url) {
        setFormData((prev) => ({ ...prev, heroImage: result.url }));
        setErrors((prev) => ({ ...prev, heroImage: undefined }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, heroImage: error?.message || 'Failed to upload campaign image.' }));
    } finally {
      event.target.value = '';
    }
  };

  const removeHeroImage = () => {
    setFormData((prev) => ({ ...prev, heroImage: '' }));
  };

  const filteredDiscounts = discounts.filter(discount => {
    const status = getStatusBadge(discount);

    // Filter logic
    if (filter === 'active' && status.label !== 'Active') return false;
    if (filter === 'inactive' && discount.isActive) return false;
    if (filter === 'expired' && status.label !== 'Expired') return false;

    // Search logic
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        discount.code.toLowerCase().includes(search) ||
        (discount.name && discount.name.toLowerCase().includes(search)) ||
        (discount.description && discount.description.toLowerCase().includes(search))
      );
    }

    return true;
  });

  return (
    <Shell>
      <FeatureGate featureId="marketing.discounts" upgradeMessage="Upgrade to access discount codes and coupon management">
      <RoleGuard
        permission="discounts:view"
        fallback={
          <div style={{
            padding: '40px',
            textAlign: 'center',
            background: 'white',
            borderRadius: '8px',
            margin: '20px'
          }}>
            <h2 style={{ color: 'var(--color-gray-700)', marginBottom: '12px' }}>Access Denied</h2>
            <p style={{ color: 'var(--color-gray-600)' }}>
              You don't have permission to view the discounts page.
            </p>
          </div>
        }
      >
        <div className="discounts-container dcv2">
        {/* Header */}
        <div className="discounts-header">
          <div>
            <h1 className="page-title">Discounts &amp; coupons</h1>
            <p className="page-subtitle">Create and manage discount codes for your customers.</p>
          </div>
          {canEdit && (
            <button className="btn-primary" onClick={() => handleOpenModal()}>
              <span className="btn-icon">+</span>
              <span>New discount</span>
            </button>
          )}
        </div>

        {/* Analytics — clean stat tiles, no rainbow gradient icons */}
        {analytics && (
          <div className="analytics-grid">
            <div className="analytics-card">
              <p className="analytics-label">Total discount given</p>
              <p className="analytics-value">{formatCurrency(analytics.totalDiscountGiven)}</p>
              <p className="analytics-sublabel">Last 30 days</p>
            </div>

            <div className="analytics-card">
              <p className="analytics-label">Redemptions</p>
              <p className="analytics-value">{analytics.totalRedemptions}</p>
              <p className="analytics-sublabel">Codes used</p>
            </div>

            <div className="analytics-card">
              <p className="analytics-label">Avg discount / order</p>
              <p className="analytics-value">{formatCurrency(analytics.avgDiscountPerOrder)}</p>
              <p className="analytics-sublabel">Per redemption</p>
            </div>

            <div className="analytics-card">
              <p className="analytics-label">Active discounts</p>
              <p className="analytics-value">{discounts.filter(d => getStatusBadge(d).label === 'Active').length}</p>
              <p className="analytics-sublabel">Currently running</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="discounts-toolbar">
          <div className="search-box">
            <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search discounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              Active
            </button>
            <button
              className={`filter-tab ${filter === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilter('inactive')}
            >
              Inactive
            </button>
            <button
              className={`filter-tab ${filter === 'expired' ? 'active' : ''}`}
              onClick={() => setFilter('expired')}
            >
              Expired
            </button>
          </div>
        </div>

        {/* Discounts List */}
        <div className="discounts-list">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading discounts...</p>
            </div>
          ) : filteredDiscounts.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <h3>No discounts found</h3>
              <p>Create your first discount code to attract more customers</p>
              <button className="btn-primary" onClick={() => handleOpenModal()}>
                Create Discount
              </button>
            </div>
          ) : (
            filteredDiscounts.map(discount => {
              const status = getStatusBadge(discount);
              const progress = getRedemptionProgress(discount);

              return (
                <div key={discount.id} className="discount-card">
                  {/* Code chip · value · name · description sit inline on the
                      left of the row. Status badge + meta float right, with
                      action buttons at the far right. Progress bar (if any)
                      wraps to a full-width strip beneath. */}
                  <div className="discount-row__lead">
                    <span className="discount-code-badge">{discount.code}</span>
                    <span className="discount-value">{getDiscountDisplay(discount)}</span>
                  </div>

                  <div className="discount-row__body">
                    {discount.name && (
                      <span className="discount-name">{discount.name}</span>
                    )}
                    {discount.description && (
                      <span className="discount-description">{discount.description}</span>
                    )}
                    <div className="discount-meta">
                      <span className="meta-item">
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>
                          {new Date(discount.startsAt).toLocaleDateString()} – {discount.endsAt ? new Date(discount.endsAt).toLocaleDateString() : 'No expiry'}
                        </span>
                      </span>
                      {discount.minOrderAmount && (
                        <span className="meta-item">Min {formatCurrency(discount.minOrderAmount)}</span>
                      )}
                      <span className="meta-item">
                        {discount.currentRedemptions} / {discount.maxRedemptions || '∞'} used
                      </span>
                    </div>
                  </div>

                  <div className="discount-row__tail">
                    <span className={`status-badge ${status.class}`}>{status.label}</span>
                    {discount.campaignType === 'SALE' && (
                      <span className="discount-type-badge sale">Store campaign</span>
                    )}
                    <span className="discount-type-badge">{discount.type.replace('_', ' ')}</span>
                  </div>

                  {canEdit && (
                    <div className="discount-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleToggleActive(discount)}
                        title={discount.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                          {discount.isActive ? (
                            <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          ) : (
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          )}
                        </svg>
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => handleOpenModal(discount)}
                        title="Edit"
                      >
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(discount)}
                        title="Delete"
                      >
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {discount.maxRedemptions > 0 && (
                    <div className="progress-bar-container">
                      <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Modal for Create/Edit */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingDiscount ? 'Edit Discount' : 'Create New Discount'}
          size="lg"
        >
          <form onSubmit={handleSubmit} className="modal-form">
                <div className="discount-stepper" aria-label="Discount setup steps">
                  {modalSteps.map((step, index) => (
                    <button
                      type="button"
                      key={step}
                      className={`discount-step ${index === currentStep ? 'is-active' : ''} ${index < currentStep ? 'is-complete' : ''}`}
                      onClick={() => setModalStep(index)}
                    >
                      <span>{index + 1}</span>
                      <strong>{step}</strong>
                    </button>
                  ))}
                </div>

                {currentStep === 0 && (
                  <div className="discount-step-panel">
                <div className="form-group">
                  <label>Discount Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="SUMMER2024"
                    disabled={!!editingDiscount}
                    className={errors.code ? 'error' : ''}
                  />
                  {errors.code && <span className="error-message">{errors.code}</span>}
                </div>

                <div className="discount-campaign-mode">
                  <button
                    type="button"
                    className={formData.campaignType === 'COUPON' ? 'is-active' : ''}
                    onClick={() => handleCampaignTypeChange('COUPON')}
                  >
                    Coupon code
                  </button>
                  <button
                    type="button"
                    className={formData.campaignType === 'SALE' ? 'is-active' : ''}
                    onClick={() => handleCampaignTypeChange('SALE')}
                  >
                    Store campaign
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name (Optional)</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Summer Sale"
                    />
                  </div>

                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                    >
                      <option value="PERCENTAGE">Percentage Off</option>
                      <option value="FIXED_AMOUNT">Fixed Amount Off</option>
                      <option value="FREE_SHIPPING">Free Shipping</option>
                      <option value="BOGO">Buy One Get One</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Value * {formData.type === 'PERCENTAGE' && '(%)'}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.value}
                      onChange={(e) => setFormData({...formData, value: e.target.value})}
                      placeholder={formData.type === 'PERCENTAGE' ? '20' : '5000'}
                      className={errors.value ? 'error' : ''}
                    />
                    {errors.value && <span className="error-message">{errors.value}</span>}
                  </div>

                  <div className="form-group">
                    <label>Min Order Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.minOrderAmount}
                      onChange={(e) => setFormData({...formData, minOrderAmount: e.target.value})}
                      placeholder="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Max Discount Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.maxDiscountAmount}
                      onChange={(e) => setFormData({...formData, maxDiscountAmount: e.target.value})}
                      placeholder="Unlimited"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe this discount..."
                    rows="3"
                  />
                </div>
                  </div>
                )}

                {formData.campaignType === 'SALE' && currentStep === 1 && (
                  <div className="discount-step-panel">
                  <div className="sale-campaign-panel">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Storefront banner title</label>
                        <input
                          type="text"
                          value={formData.bannerTitle}
                          onChange={(e) => setFormData({...formData, bannerTitle: e.target.value})}
                          placeholder="Black Friday Sale"
                        />
                      </div>
                      <div className="form-group">
                        <label>Badge text</label>
                        <input
                          type="text"
                          value={formData.badgeText}
                          onChange={(e) => setFormData({...formData, badgeText: e.target.value.toUpperCase()})}
                          placeholder="SALE"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Banner subtitle</label>
                      <input
                        type="text"
                        value={formData.bannerSubtitle}
                        onChange={(e) => setFormData({...formData, bannerSubtitle: e.target.value})}
                        placeholder="Deals end soon. Prices update automatically at checkout."
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.showOnStorefront}
                            onChange={(e) => setFormData({...formData, showOnStorefront: e.target.checked})}
                          />
                          <span>Show announcement bar on storefront</span>
                        </label>
                      </div>
                      <div className="form-group">
                        <label>Priority</label>
                        <input
                          type="number"
                          value={formData.priority}
                          onChange={(e) => setFormData({...formData, priority: e.target.value})}
                          placeholder="100"
                        />
                        <small>Lower number shows first when multiple sales are active.</small>
                      </div>
                    </div>

                    <div className="campaign-hero-panel">
                      <div className="campaign-hero-panel__header">
                        <div>
                          <h4>Campaign hero</h4>
                          <p>Temporarily replaces the storefront hero while this sale is active.</p>
                        </div>
                        <label className="campaign-hero-toggle">
                          <input
                            type="checkbox"
                            checked={formData.heroEnabled}
                            onChange={(e) => setFormData({...formData, heroEnabled: e.target.checked})}
                          />
                          <span>Use campaign hero</span>
                        </label>
                      </div>

                      {formData.heroEnabled && (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Hero title</label>
                              <input
                                type="text"
                                value={formData.heroTitle}
                                onChange={(e) => setFormData({...formData, heroTitle: e.target.value})}
                                placeholder="Black Friday Sale"
                              />
                            </div>
                            <div className="form-group">
                              <label>CTA text</label>
                              <input
                                type="text"
                                value={formData.heroCtaText}
                                onChange={(e) => setFormData({...formData, heroCtaText: e.target.value})}
                                placeholder="Shop deals"
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Hero subtitle</label>
                            <input
                              type="text"
                              value={formData.heroSubtitle}
                              onChange={(e) => setFormData({...formData, heroSubtitle: e.target.value})}
                              placeholder="Up to 50% off selected products. Limited time only."
                            />
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>CTA link</label>
                              <input
                                type="text"
                                value={formData.heroCtaLink}
                                onChange={(e) => setFormData({...formData, heroCtaLink: e.target.value})}
                                placeholder="/shop"
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Hero image (optional)</label>
                            <div className="campaign-hero-image-control">
                              {formData.heroImage ? (
                                <div className="campaign-hero-image-preview">
                                  <img src={formData.heroImage} alt="Campaign hero preview" />
                                  <button type="button" onClick={removeHeroImage}>
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="campaign-hero-image-empty">
                                  <span>No image selected</span>
                                  <small>The hero still works with a clean solid-color offer card.</small>
                                </div>
                              )}

                              <div className="campaign-hero-image-actions">
                                <label className="campaign-hero-upload-btn">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleHeroImageUpload}
                                    disabled={mediaUploading}
                                  />
                                  {mediaUploading ? 'Uploading...' : 'Upload image'}
                                </label>
                                <button type="button" className="campaign-hero-library-btn" onClick={openHeroMediaPicker}>
                                  Choose from media
                                </button>
                              </div>
                            </div>
                            {errors.heroImage && <span className="error-message">{errors.heroImage}</span>}
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Background color</label>
                              <input
                                type="color"
                                value={formData.heroBackgroundColor || '#0d7463'}
                                onChange={(e) => setFormData({...formData, heroBackgroundColor: e.target.value})}
                              />
                            </div>
                            <div className="form-group">
                              <label>Text color</label>
                              <input
                                type="color"
                                value={formData.heroTextColor || '#ffffff'}
                                onChange={(e) => setFormData({...formData, heroTextColor: e.target.value})}
                              />
                            </div>
                            <div className="form-group checkbox">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={formData.showPromoCode}
                                  onChange={(e) => setFormData({...formData, showPromoCode: e.target.checked})}
                                />
                                <span>Show promo code on hero</span>
                              </label>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  </div>
                )}

                {formData.campaignType === 'SALE' && currentStep === 2 && (
                  <div className="discount-step-panel">
                  <div className="sale-scope-panel">
                    <div className="sale-scope-header">
                      <div>
                        <h4>Sale scope</h4>
                        <p>Leave empty for a whole-store sale, or select specific products/categories.</p>
                      </div>
                      {(formData.applicableProducts.length > 0 || formData.applicableCategories.length > 0) && (
                        <button type="button" className="scope-clear-btn" onClick={clearSaleScope}>
                          Clear
                        </button>
                      )}
                    </div>

                    {categories.length > 0 && (
                      <div className="scope-section">
                        <span className="scope-label">Categories</span>
                        <div className="scope-chip-grid">
                          {categories.map((category) => (
                            <button
                              type="button"
                              key={category}
                              className={`scope-chip ${formData.applicableCategories.includes(category) ? 'is-selected' : ''}`}
                              onClick={() => toggleCategoryScope(category)}
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {products.length > 0 && (
                      <div className="scope-section">
                        <span className="scope-label">Products</span>
                        <div className="product-scope-list">
                          {products.slice(0, 80).map((product) => (
                            <label key={product.id || product._id} className="product-scope-row">
                              <input
                                type="checkbox"
                                checked={formData.applicableProducts.includes(product.id || product._id)}
                                onChange={() => toggleProductScope(product.id || product._id)}
                              />
                              <span>{product.name}</span>
                              <em>{formatCurrency(product.price || 0)}</em>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                )}

                {currentStep === modalSteps.length - 1 && (
                  <div className="discount-step-panel">
                <div className="form-row">
                  <div className="form-group">
                    <label>Max Redemptions</label>
                    <input
                      type="number"
                      value={formData.maxRedemptions}
                      onChange={(e) => setFormData({...formData, maxRedemptions: e.target.value})}
                      placeholder="0 = Unlimited"
                    />
                    <small>0 means unlimited usage</small>
                  </div>

                  <div className="form-group">
                    <label>Usage Per Customer</label>
                    <input
                      type="number"
                      value={formData.usagePerCustomer}
                      onChange={(e) => setFormData({...formData, usagePerCustomer: e.target.value})}
                      placeholder="1"
                    />
                    <small>How many times each customer can use</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date & Time *</label>
                    <input
                      type="datetime-local"
                      value={formData.startsAt}
                      onChange={(e) => setFormData({...formData, startsAt: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label>End Date & Time</label>
                    <input
                      type="datetime-local"
                      value={formData.endsAt}
                      onChange={(e) => setFormData({...formData, endsAt: e.target.value})}
                      className={errors.endsAt ? 'error' : ''}
                    />
                    {errors.endsAt && <span className="error-message">{errors.endsAt}</span>}
                    <small>Leave empty for no expiry</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      />
                      <span>Active (can be used immediately)</span>
                    </label>
                  </div>

                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isPublic}
                        disabled={formData.campaignType === 'SALE'}
                        onChange={(e) => setFormData({...formData, isPublic: e.target.checked})}
                      />
                      <span>Public (visible to all customers)</span>
                    </label>
                  </div>
                </div>

                {errors.submit && (
                  <div className="error-alert">{errors.submit}</div>
                )}
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  {currentStep > 0 && (
                    <button type="button" className="btn-secondary" onClick={() => setModalStep((step) => Math.max(step - 1, 0))}>
                      Back
                    </button>
                  )}
                  <button type="submit" className="btn-primary">
                    {isFinalStep ? (editingDiscount ? 'Update Discount' : 'Create Discount') : 'Continue'}
                  </button>
                </div>
              </form>
        </Modal>

        <Modal
          isOpen={showHeroMediaPicker}
          onClose={() => setShowHeroMediaPicker(false)}
          title="Choose campaign image"
          size="lg"
        >
          <div className="campaign-media-picker">
            <p>Choose an image from your media library.</p>
            {mediaList.filter(media => media.type === 'IMAGE' || media.mimeType?.startsWith('image/')).length === 0 ? (
              <div className="campaign-media-picker__empty">
                <span>No images found</span>
                <small>Upload a campaign image from the sale form first.</small>
              </div>
            ) : (
              <div className="campaign-media-picker__grid">
                {mediaList
                  .filter(media => media.type === 'IMAGE' || media.mimeType?.startsWith('image/'))
                  .map((media) => (
                    <button
                      type="button"
                      key={media.id}
                      className={`campaign-media-picker__item ${formData.heroImage === media.url ? 'is-selected' : ''}`}
                      onClick={() => handleHeroMediaSelect(media.url)}
                    >
                      <img src={media.url} alt={media.alt || media.originalName || 'Campaign media'} />
                    </button>
                  ))}
              </div>
            )}
          </div>
        </Modal>
        </div>
      </RoleGuard>
      </FeatureGate>
    </Shell>
  );
}
